import { copy } from "../copy";
import { formatMoney } from "../format";
import { contestHref, pickKey, type LandingLiveContest, type Pick } from "../feed";
import type { BeatPlayer } from "../audio";
import Countdown from "./Countdown";
import Icon from "./Icon";
import { PickCard } from "./PickCards";
import TierSplit from "./TierSplit";
import { asTier } from "../tiers";

type LineProps = { contest: LandingLiveContest; player: BeatPlayer; onBack: (pick: Pick) => void };

// A game line: cover with its badges, name, the pool and countdown, and one
// row per producer. A closed contest keeps its pool but offers no Back. The
// fee is on the contest page and the slip, not on the card.
function ContestLine({ contest, player, onBack }: LineProps) {
  const open = new Date(contest.ends_at).getTime() > Date.now();
  const funded = contest.pool_cents > 0;
  return (
    <li
      data-testid="contest-card"
      className="flex w-[322px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-ground-edge bg-ground md:w-[430px]"
    >
      <a
        href={contestHref(contest.slug)}
        tabIndex={-1}
        aria-hidden
        className="relative block aspect-[16/9] w-full overflow-hidden"
      >
        {contest.cover_url ? (
          <img
            data-testid="card-cover"
            src={contest.cover_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,rgba(247,201,72,0.18),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(43,217,107,0.14),transparent_50%)] bg-ground-raised">
            <Icon name="disc" className="h-12 w-12 text-chrome-dim/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ground via-ground/10 to-transparent" />
      </a>

      <div className="relative -mt-10 flex flex-1 flex-col px-4 pb-4 md:px-5 md:pb-5">
        <div className="flex flex-wrap items-center gap-2">
          {open && (
            <span
              data-testid="live-badge"
              className="flex items-center gap-1.5 rounded-full bg-ground/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-money backdrop-blur-sm"
            >
              <span aria-hidden className="bf-live-dot h-1.5 w-1.5 rounded-full bg-money" />
              {copy.live.badge}
            </span>
          )}
          {contest.genre && (
            <span className="rounded-full border border-ground-edge bg-ground/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-chrome-dim backdrop-blur-sm">
              {contest.genre}
            </span>
          )}
          {contest.is_seeded && (
            <span
              data-testid="seeded-label"
              className="rounded-full border border-gold/40 bg-ground/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold backdrop-blur-sm"
            >
              {copy.live.seeded}
            </span>
          )}
        </div>

        <h3 className="mt-3 font-headline text-2xl uppercase leading-tight tracking-[-0.01em] text-chrome">{contest.name}</h3>

        <div
          data-testid="card-pool"
          className={`mt-4 rounded-xl border px-4 py-3 ${funded ? "border-money/40 bg-money/10 shadow-money" : "border-ground-edge bg-ground-raised"}`}
        >
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${funded ? "text-money" : "text-chrome-dim"}`}>
                {copy.live.pool}
              </p>
              {funded ? (
                <p className="bf-tabular mt-1 font-headline text-4xl leading-none text-money md:text-5xl">
                  {formatMoney(contest.pool_cents)}
                </p>
              ) : (
                <p className="mt-1 text-sm font-bold text-chrome-dim">{copy.live.emptyPool}</p>
              )}
            </div>
            <Countdown endsAt={contest.ends_at} />
          </div>
        </div>

        {contest.tier && (
          <TierSplit
            tier={asTier(contest.tier)}
            split={{
              rake_bps: contest.rake_bps ?? 0,
              producer_bps: contest.producer_bps ?? 0,
              backing_bps: contest.backing_bps ?? 0,
            }}
          />
        )}

        {open && contest.outcomes.length > 0 && (
          <div className="mt-5 flex-1 space-y-2 border-t border-ground-edge pt-4">
            {contest.outcomes.map((outcome) => {
              const pick = { contest, outcome };
              return <PickCard key={pickKey(pick)} pick={pick} player={player} onBack={onBack} variant="line" />;
            })}
          </div>
        )}
        {!open && (
          <p className="mt-5 flex-1 border-t border-ground-edge pt-4 text-xs font-bold uppercase tracking-[0.14em] text-chrome-dim">
            {copy.live.picksClosed}
          </p>
        )}

        <a
          href={contestHref(contest.slug)}
          className="bf-focusable mt-4 flex h-10 items-center justify-center rounded-md border border-ground-edge text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim transition-colors hover:border-gold/50 hover:text-gold"
        >
          {copy.live.openContest}
        </a>
      </div>
    </li>
  );
}

export default function LiveContests({
  contests,
  player,
  onBack,
}: {
  contests: LandingLiveContest[];
  player: BeatPlayer;
  onBack: (pick: Pick) => void;
}) {
  // No contests, no section, and no skeleton pretending otherwise.
  if (contests.length === 0) return null;
  const [lead, tail] = copy.live.heading;
  return (
    <section
      id="live-contests"
      data-testid="live-contests"
      className="border-t border-ground-edge bg-ground-raised px-4 py-20 md:px-6 md:py-28"
    >
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4 md:mb-14">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{copy.live.eyebrow}</p>
            <h2 className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl">
              {lead} <span className="text-gold">{tail}</span>
            </h2>
          </div>
          <p className="hidden text-xs uppercase tracking-[0.18em] text-chrome-dim md:block">{copy.live.scrollHint}</p>
          <a
            href="/contests"
            className="bf-focusable hidden whitespace-nowrap text-xs font-bold uppercase tracking-[0.16em] text-money transition-colors hover:text-money-deep md:inline-flex"
          >
            {copy.live.browseAll}
          </a>
        </div>
        {/* relative: the countdowns' screen-reader text is absolutely
            positioned, and would escape the scroller and widen the page. */}
        <ul className="bf-no-scrollbar relative -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 md:mx-0 md:px-0">
          {contests.map((c) => (
            <ContestLine key={c.slug} contest={c} player={player} onBack={onBack} />
          ))}
        </ul>
      </div>
    </section>
  );
}
